import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { useToggle } from "usehooks-ts";
import { object, ref, string } from "yup";

import Button from "components/Button";
import Input from "components/Input";
import StatusModal from "components/StatusModal";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { IDeleteAccount } from "models/UserSetting";
import { deleteAccountAPI } from "services/api/user";
import { AppDispatch } from "store";
import { logOut } from "store/auth";
import { updateUserEmail } from "store/user/actions";
import { selectUserDetails } from "store/user/selector";
import { useAnalytics } from "hooks/useAnalytics";

interface IUpdateEmailForm {
  email?: string;
  confirmEmail?: string;
}

const emailFormSchema = object<IUpdateEmailForm>({
  email: string().required("This field is required"),
  confirmEmail: string()
    .oneOf([ref("email")], "Emails must match")
    .required("This field is required")
});

export default function UpdateEmail() {
  const dispatch = useDispatch<AppDispatch>();
  const userDetails = useSelector(selectUserDetails);
  const { trackAllEvents } = useAnalytics();
  const [isSuccessModalOpen, toggleSuccessModal] = useToggle();

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit
  } = useForm<IUpdateEmailForm>({
    resolver: yupResolver(emailFormSchema),
    values: {
      email: userDetails?.email
    }
  });

  const onSubmit = async (data: IUpdateEmailForm) => {
    const formData = {
      userId: userDetails?.id as string,
      email: data.email!
    };

    try {
      await dispatch(updateUserEmail(formData)).then(({ payload }: { payload: any }) => {
        if (payload.id === userDetails?.id) {
          showSuccessMessage("Successfully updated email");
        } else {
          showErrorMessage("Failed to update email");
        }
      });
    } catch {
      showErrorMessage("Failed to update email");
    }
  };

  const deleteAccount = async () => {
    const data: IDeleteAccount = {
      name: userDetails?.dba,
      email: userDetails?.email,
      message: "Delete Account requested please contact user"
    };

    try {
      const response = await deleteAccountAPI(data);
      if (response?.data.user) {
        toggleSuccessModal();
      } else {
        showErrorMessage("Failed to delete account");
      }
    } catch {
      showErrorMessage("Failed to delete account");
    }
  };

  const userLogOut = () => {
    toggleSuccessModal();
    dispatch(logOut());
    trackAllEvents("settings_logout");
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
      <span>Update Email</span>
      <Input
        type="email"
        name="email"
        label="&nbsp;&nbsp;Email"
        register={register}
        error={errors.email}
        disabled
      />
      <Input
        name="confirmEmail"
        label="&nbsp;&nbsp;Confirm Email"
        type="email"
        register={register}
        error={errors.confirmEmail}
        disabled
      />

      <div className="-mt-2 text-sm text-text">
        <span>
          For security, you cannot change your email your email here. Please contact support (
        </span>
        <a href="mailto:support@comfreight.com" className="underline text-info">
          support@comfreight.com
        </a>
        <span>)</span>
      </div>

      <div className="flex gap-3 desktop:gap-4 desktop:justify-end">
        <Button
          type="submit"
          size="lg"
          disabled
          loading={isSubmitting}
          className="flex-1 desktop:flex-none desktop:!px-10 desktop:!w-fit desktop:!h-12">
          Update Email
        </Button>

        <Button
          type="button"
          size="lg"
          onClick={deleteAccount}
          className="flex-1 desktop:flex-none desktop:!px-10 desktop:!w-fit desktop:!h-12">
          Delete Account
        </Button>
      </div>

      <StatusModal
        title="Success"
        isModalOpen={isSuccessModalOpen}
        toggleModal={toggleSuccessModal}
        content="Successfully delete account"
        backdropClick={userLogOut}
      />
    </form>
  );
}
